import React from 'react';

// ─── Base shimmer animation ───────────────────────────────────────────────────
// Used internally by all skeleton components.
const Shimmer = ({ className = '' }) => (
  <div
    className={`relative overflow-hidden bg-white/[0.04] rounded-xl before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/[0.06] before:to-transparent ${className}`}
  >
    <style>{`
      @keyframes shimmer {
        100% { transform: translateX(100%); }
      }
    `}</style>
  </div>
);

// ─── Event Card Skeleton ──────────────────────────────────────────────────────
// Matches the shape of EventCard.js
export const EventCardSkeleton = () => (
  <div className="bg-white/[0.02] rounded-[2rem] border border-white/[0.06] overflow-hidden flex flex-col h-full">
    {/* Banner */}
    <Shimmer className="h-52 md:h-64 rounded-none" />
    {/* Content */}
    <div className="p-6 md:p-8 flex flex-col flex-grow space-y-4">
      <Shimmer className="h-3 w-20 rounded-full" />
      <Shimmer className="h-6 w-3/4 rounded-xl" />
      <Shimmer className="h-4 w-full rounded-xl" />
      <Shimmer className="h-4 w-2/3 rounded-xl" />
      <div className="flex gap-4 pt-2 pb-4 border-b border-white/[0.05]">
        <div className="space-y-1.5"><Shimmer className="h-5 w-6 rounded-lg" /><Shimmer className="h-2.5 w-14 rounded-full" /></div>
        <div className="space-y-1.5"><Shimmer className="h-5 w-6 rounded-lg" /><Shimmer className="h-2.5 w-14 rounded-full" /></div>
      </div>
      <Shimmer className="h-11 w-full rounded-xl" />
    </div>
  </div>
);

// ─── Homepage Events Grid Skeleton ────────────────────────────────────────────
export const HomepageSkeleton = () => (
  <div className="max-w-7xl mx-auto px-4 md:px-6">
    {/* Hero text skeleton */}
    <div className="pt-28 md:pt-40 pb-16 space-y-5">
      <Shimmer className="h-3 w-32 rounded-full" />
      <Shimmer className="h-14 md:h-20 w-4/5 rounded-2xl" />
      <Shimmer className="h-14 md:h-20 w-3/5 rounded-2xl" />
      <Shimmer className="h-5 w-72 rounded-xl mt-4" />
    </div>
    {/* Ad strip skeleton */}
    <Shimmer className="h-16 sm:h-20 w-full rounded-2xl mb-8" />
    {/* Cards label */}
    <Shimmer className="h-3 w-24 rounded-full mb-8" />
    {/* Cards grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {[1, 2, 3].map(i => <EventCardSkeleton key={i} />)}
    </div>
  </div>
);

// ─── Event Detail / Hero Skeleton ─────────────────────────────────────────────
export const EventDetailSkeleton = () => (
  <div>
    {/* Hero */}
    <div className="relative w-full bg-white/[0.03]" style={{ minHeight: '60vh' }}>
      <Shimmer className="w-full h-full absolute inset-0 rounded-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-32 pb-16 md:pt-40 md:pb-24 flex flex-col justify-end space-y-4" style={{ minHeight: '60vh' }}>
        <Shimmer className="h-3 w-24 rounded-full" />
        <Shimmer className="h-3 w-40 rounded-full" />
        <Shimmer className="h-14 md:h-20 w-3/4 rounded-2xl" />
        <Shimmer className="h-14 md:h-20 w-1/2 rounded-2xl" />
        <Shimmer className="h-5 w-96 max-w-full rounded-xl" />
        <Shimmer className="h-5 w-72 rounded-xl" />
      </div>
    </div>
    {/* Content below hero */}
    <div className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
      <Shimmer className="h-16 sm:h-20 w-full rounded-2xl my-8" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Segments */}
        <div className="lg:col-span-2 space-y-5">
          <Shimmer className="h-3 w-28 rounded-full" />
          <Shimmer className="h-7 w-48 rounded-xl" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => <SegmentCardSkeleton key={i} />)}
          </div>
        </div>
        {/* Sidebar */}
        <div className="space-y-5">
          <Shimmer className="h-36 rounded-3xl" />
          <Shimmer className="h-48 rounded-3xl" />
          <Shimmer className="h-32 rounded-3xl" />
        </div>
      </div>
    </div>
  </div>
);

// ─── Segment Card Skeleton ────────────────────────────────────────────────────
export const SegmentCardSkeleton = () => (
  <div className="bg-white/[0.03] rounded-3xl border border-white/[0.05] overflow-hidden">
    <Shimmer className="h-36 rounded-none" />
    <div className="p-6 space-y-3">
      <div className="flex justify-between">
        <Shimmer className="h-5 w-2/3 rounded-xl" />
        <Shimmer className="h-5 w-12 rounded-xl" />
      </div>
      <Shimmer className="h-3.5 w-full rounded-xl" />
      <Shimmer className="h-3.5 w-4/5 rounded-xl" />
      <div className="flex justify-between pt-2">
        <Shimmer className="h-6 w-16 rounded-full" />
        <Shimmer className="h-4 w-20 rounded-full" />
      </div>
    </div>
  </div>
);

// ─── Registration Page Skeleton ───────────────────────────────────────────────
export const RegistrationSkeleton = () => (
  <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 pb-20">
    {/* Breadcrumb */}
    <div className="flex items-center gap-2 mb-8">
      {[1, 2, 3].map(i => <><Shimmer key={i} className="h-3 w-16 rounded-full" />{i < 3 && <Shimmer className="h-3 w-2 rounded-full" />}</>)}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Form skeleton */}
      <div className="lg:col-span-2 space-y-6">
        <Shimmer className="h-40 rounded-3xl" />
        <Shimmer className="h-14 rounded-2xl" />
        <div className="grid grid-cols-2 gap-4">
          <Shimmer className="h-14 rounded-2xl" />
          <Shimmer className="h-14 rounded-2xl" />
        </div>
        <Shimmer className="h-14 rounded-2xl" />
        <Shimmer className="h-32 rounded-2xl" />
        <Shimmer className="h-14 rounded-2xl" />
        <Shimmer className="h-16 rounded-2xl" />
      </div>
      {/* Sidebar skeleton */}
      <div className="space-y-5">
        <Shimmer className="h-48 rounded-3xl" />
        <Shimmer className="h-40 rounded-3xl" />
        <Shimmer className="h-32 rounded-3xl" />
      </div>
    </div>
  </div>
);

// ─── Portal Loading Skeleton ──────────────────────────────────────────────────
export const PortalLoadingSkeleton = () => (
  <div className="flex min-h-screen bg-[#050505]">
    {/* Sidebar */}
    <div className="w-64 border-r border-white/5 p-6 space-y-4">
      <Shimmer className="h-6 w-32 rounded-xl" />
      <Shimmer className="h-10 w-full rounded-xl mt-8" />
      {[1, 2, 3, 4, 5].map(i => <Shimmer key={i} className="h-10 w-full rounded-xl" />)}
    </div>
    {/* Main */}
    <div className="flex-1 p-10 space-y-6">
      <Shimmer className="h-8 w-48 rounded-xl" />
      <div className="grid grid-cols-3 gap-5">
        {[1, 2, 3].map(i => <Shimmer key={i} className="h-28 rounded-[2rem]" />)}
      </div>
      <Shimmer className="h-64 rounded-[2rem]" />
    </div>
  </div>
);

export default Shimmer;